import { Button } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import Spinner from 'components/Spinner'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useStyletron } from 'styletron-react'
import BrandVoiceBody from './BrandVoiceBody'
import {
  collapseBrandVoiceText,
  getDefaultBrandVoiceText,
} from './brandVoiceText'
import BrandVoiceUploadView from './BrandVoiceUploadView'
import ConfigModalHost from './ConfigModalHost'
import { useRecoveryConfigDraft } from './RecoveryConfigDraftContext'
import useBrandVoiceGenerate from './useBrandVoiceGenerate'

const tTabs = buildTranslate('recovery_ai.settings.tabs')
const tModal = buildTranslate('recovery_ai.config_modal')
const tBrandVoice = buildTranslate('recovery_ai.brand_voice')

// ModalContainer wraps its content in p-xl ($spacer * 3 = 48px). The brand-voice
// header rule is drawn full-bleed by cancelling that horizontal padding.
const MODAL_PADDING_PX = 48

type BrandVoiceModalProps = {
  onClose: () => void
  onSaved: () => void
}

// W7 (FR-6) brand-voice modal: a self-contained two-view state machine
// (edit | upload) with its OWN ConfigModalHost, so it can swap the footer +
// header in place without disturbing the other three config modals. It consumes
// the surrounding RecoveryConfigDraftProvider (edits stage locally; Save commits
// one PUT). Unlike the other rows it has NO "Save and test", and it stops
// staging brand_voice_mode — nexus derives it read-only. Must be a provider child.
//
// The staged-file + derive machine lives in the shared useBrandVoiceGenerate hook
// (also used by the Settings pane) so the two surfaces can't drift.
const BrandVoiceModal = ({ onClose, onSaved }: BrandVoiceModalProps) => {
  const [css] = useStyletron()
  const {
    draft,
    setField,
    canManage,
    isDirty,
    isConfigLoading,
    isConfigError,
    isSaving,
    isSaveError,
    commit,
    rollback,
  } = useRecoveryConfigDraft()

  const {
    files,
    clientError,
    deriveError,
    isDeriving,
    errorCopyKey,
    addFiles,
    removeFile,
    clearClientError,
    clearDeriveError,
    generate,
    reset: resetGenerate,
  } = useBrandVoiceGenerate()

  const [view, setView] = useState<'edit' | 'upload'>('edit')
  const [text, setText] = useState('')
  // seedWasBlank is a per-open snapshot here, NOT the live derivation the Settings
  // pane uses. It's safe as a snapshot because the modal remounts on every open
  // and unmounts on Save — there's no "save then keep editing" within one open, so
  // it can't go stale the way the persistent pane's could.
  const [seedWasBlank, setSeedWasBlank] = useState(true)

  // Hydrate ONCE. RTK hands back a new `draft` ref on every background refetch,
  // so keying off `draft` without the ref would clobber keystrokes mid-edit.
  // Wait for a USABLE config — isConfigError too, not just isConfigLoading, since a failed GET
  // also clears loading and would latch the default over a real persisted voice, with
  // seedWasBlank set so a later edit landing back on the default collapses it to '' on Save.
  const hasHydrated = useRef(false)
  useEffect(() => {
    if (hasHydrated.current || isConfigLoading || isConfigError) return
    hasHydrated.current = true
    const persisted = draft.brand_voice_text ?? ''
    const blank = persisted.trim() === ''
    setSeedWasBlank(blank)
    setText(blank ? getDefaultBrandVoiceText() : persisted)
  }, [draft, isConfigLoading, isConfigError])

  const handleTextChange = useCallback(
    (next: string) => {
      setText(next)
      // Stage the COLLAPSED value: an unedited default stages '' so it isn't
      // dirty and brand_voice_mode keeps deriving to 'default'. brand_voice_mode
      // is NOT staged — nexus derives it read-only and drops any sent value.
      setField('brand_voice_text', collapseBrandVoiceText(next, seedWasBlank))
      // Editing supersedes a prior failed-generate banner, so it can't sit above
      // the merchant contradicting what they're typing.
      clearDeriveError()
    },
    [setField, seedWasBlank, clearDeriveError]
  )

  const openUpload = useCallback(() => {
    clearDeriveError()
    setView('upload')
  }, [clearDeriveError])

  const backToEdit = useCallback(() => {
    setView('edit')
    clearClientError()
  }, [clearClientError])

  const handleGenerate = useCallback(async () => {
    // State 4 is the EDIT view with the skeleton, not the upload view.
    setView('edit')
    await generate(derived => {
      setText(derived)
      setField(
        'brand_voice_text',
        collapseBrandVoiceText(derived, seedWasBlank)
      )
    })
  }, [generate, setField, seedWasBlank])

  const handleSave = useCallback(async () => {
    clearDeriveError()
    if (await commit()) onSaved()
  }, [commit, onSaved, clearDeriveError])

  const handleCancel = useCallback(() => {
    resetGenerate()
    rollback()
    onClose()
  }, [resetGenerate, rollback, onClose])

  // 1px header rule under the title / Back row, edge-to-edge across the card.
  // ModalContainer renders no rule of its own and has many consumers, so draw it
  // here and cancel the modal's p-xl horizontal padding to reach the card edges.
  const headerRule = (
    <div
      aria-hidden="true"
      className={css({
        borderBottom: `1px solid ${palette.grey20}`,
        marginLeft: `-${MODAL_PADDING_PX}px`,
        marginRight: `-${MODAL_PADDING_PX}px`,
        marginBottom: '24px',
      })}
    />
  )

  const isUpload = view === 'upload'

  let body: React.ReactNode
  if (isUpload) {
    body = (
      <BrandVoiceUploadView
        files={files}
        onAddFiles={addFiles}
        onRemoveFile={removeFile}
        clientError={clientError}
      />
    )
  } else if (isConfigLoading) {
    // Withhold the editing body until the config GET resolves, so the merchant
    // never edits — or saves over — defaults before their real config loads.
    body = (
      <div role="status" aria-label={tModal('loading')}>
        <Spinner isLoading size="4x" className="text-center" />
      </div>
    )
  } else if (isConfigError) {
    body = (
      <Notification kind="negative" size="full">
        {tModal('load_error')}
      </Notification>
    )
  } else {
    body = (
      <>
        {isSaveError && (
          <div className="mb-m">
            <Notification kind="negative" size="full">
              {tModal('save_error')}
            </Notification>
          </div>
        )}
        {/* D6 parity: the Settings pane leads with this description; mirror it. */}
        <div className="mb-l">
          <Text.BodyText3 color="grey70">
            {tBrandVoice('description')}
          </Text.BodyText3>
        </div>
        {deriveError && (
          <div className="mb-m">
            <Notification kind="negative" size="full">
              {tBrandVoice(errorCopyKey)}
            </Notification>
          </div>
        )}
        <BrandVoiceBody
          value={text}
          onChange={handleTextChange}
          onGenerate={openUpload}
          isDeriving={isDeriving}
          derivingFilenames={files.map(f => f.name)}
        />
      </>
    )
  }

  const footer = isUpload ? (
    <Button
      kind="primary"
      onClick={handleGenerate}
      disabled={files.length === 0 || isDeriving}
      isLoading={isDeriving}
    >
      {tBrandVoice('generate')}
    </Button>
  ) : (
    <>
      <Button kind="secondary" onClick={handleCancel} disabled={isSaving}>
        {tModal('cancel')}
      </Button>
      <Button
        kind="primary"
        className="ml-s"
        onClick={handleSave}
        // Gate Save while deriving: nothing valid to save mid-derivation. The
        // mock shows it enabled — deliberate deviation, called out in the PR body.
        //
        // `canManage` is here because this row short-circuits ConfigRowModal, so the
        // shared shell's identical term never runs for brand voice — and
        // BrandVoiceBody's textarea locks on isDeriving alone, so a view-only
        // operator can type and arm this button. Not defence in depth: it is the
        // ONLY client-side gate this row has.
        disabled={!isDirty || !canManage || isDeriving || isSaving}
        isLoading={isSaving}
      >
        {tBrandVoice('save_changes')}
      </Button>
    </>
  )

  return (
    <ConfigModalHost
      isOpen
      title={isUpload ? undefined : tTabs('brand_voice')}
      ariaLabel={isUpload ? tBrandVoice('upload_view_aria_label') : undefined}
      onBack={isUpload ? backToEdit : undefined}
      onCancel={handleCancel}
      isSaving={isSaving}
      // L3: the in-flight dismiss block covers derive as well as save. Tearing
      // the modal down mid-derive runs handleCancel → resetGenerate(), throwing
      // away the derivation the merchant is waiting on, with nothing on screen
      // to explain it. isBusy gates the ACCIDENTAL dismissals (X, scrim, Escape)
      // only; the footer Cancel below stays live on purpose, because this modal
      // renders no abort-derive control of its own and Cancel is the merchant's
      // one way out of a slow derive.
      isBusy={isDeriving}
      footer={footer}
    >
      {headerRule}
      {body}
    </ConfigModalHost>
  )
}

export default BrandVoiceModal
